import { VITE_API_URL, VITE_APP_BASENAME } from '@/config';
import {
  convertLowerCamelToSnake,
  convertSnakeToLowerCamel,
  getCookie,
  isPS,
  logger,
} from '@/utils';
import axios, { type AxiosRequestConfig, isAxiosError } from 'axios';
import { ApiErrorCodeLabelModifier } from './error-codes';

declare module 'axios' {
  export interface AxiosRequestConfig {
    errorContext?: Error;
  }
}

const getErrorMessage = (err: any) => {
  return err.response?.data.error_code
    ? ApiErrorCodeLabelModifier[err.response.data.error_code]
    : `${err.message}. ${err.config?.method?.toUpperCase()} ${err.config?.url}`;
};

const axiosAuthenticated = axios.create({});
axiosAuthenticated.interceptors.request.use((request) => {
  request.errorContext = new Error();
  const authToken = getCookie('access_token');
  if (authToken) {
    request.headers.Authorization = `Bearer ${authToken}`;
  }
  return request;
});
axiosAuthenticated.interceptors.response.use(
  (res) => {
    const traceLog = [res.config.method?.toUpperCase(), res.config.url];
    logger.trace(traceLog.join(' '), {
      query: res.config.params,
      body: res.config.data,
      data: res.data,
    });
    return res.data;
  },
  async (err) => {
    const { errorContext, ...config } = err.config ?? {};
    if (isAxiosError(err)) {
      if (err.response?.status === 401) {
        logger.debug('UNAUTHENTICATED: cookie deleted', err.response.data, err);
        // TODO: This is a provisional solution. We have to reconsider authentication architecture.
        document.cookie =
          'access_token=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      }
      const originalStackTrace = errorContext?.stack;
      if (originalStackTrace) {
        err.stack = `${err.stack}\nCaused by:\n${originalStackTrace.split('\n').slice(2).join('\n')}`;
      }
      err.config = config;
      err.message = getErrorMessage(err);
    }
    return await Promise.reject(err);
  },
);

const axiosUnauthenticated = axios.create({});
axiosUnauthenticated.interceptors.request.use((request) => {
  request.errorContext = new Error();
  return request;
});
axiosUnauthenticated.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    const { errorContext, ...config } = err.config ?? {};

    const originalStackTrace = errorContext?.stack;
    if (originalStackTrace) {
      err.stack = `${err.stack}\nCaused by: ${originalStackTrace}`;
    }
    err.config = config;
    err.message = getErrorMessage(err);

    return await Promise.reject(err);
  },
);

const baseConfig: AxiosRequestConfig = {
  baseURL: VITE_API_URL + VITE_APP_BASENAME,
};

export const axiosGet = async (url: string, data?: object) => {
  const handler = isPS() ? axiosUnauthenticated : axiosAuthenticated;
  return convertSnakeToLowerCamel(
    await handler.get(url, {
      ...baseConfig,
      params: data ? convertLowerCamelToSnake(data) : undefined,
      paramsSerializer: { indexes: null },
    }),
  );
};

export const axiosPost = async (
  url: string,
  data: object | null,
  headers?: object,
  isRaw = false,
) => {
  const handler = isPS() ? axiosUnauthenticated : axiosAuthenticated;
  return convertSnakeToLowerCamel(
    await handler.post(url, isRaw ? data : convertLowerCamelToSnake(data), {
      ...baseConfig,
      headers,
    }),
  );
};

export const unauthenticatedPost = async (url: string, data: object | null) => {
  return convertSnakeToLowerCamel(
    await axiosUnauthenticated.post(url, convertLowerCamelToSnake(data), {
      ...baseConfig,
    }),
  );
};

export const axiosPatch = async (url: string, data: object | null) => {
  return convertSnakeToLowerCamel(
    await axiosAuthenticated.patch(url, convertLowerCamelToSnake(data), {
      ...baseConfig,
    }),
  );
};

export const axiosPut = async (url: string, data: object | null) => {
  return convertSnakeToLowerCamel(
    await axiosAuthenticated.put(url, convertLowerCamelToSnake(data), {
      ...baseConfig,
    }),
  );
};

export const axiosDelete = async (url: string) => {
  return convertSnakeToLowerCamel(
    await axiosAuthenticated.delete(url, { ...baseConfig }),
  );
};
